import React, { useEffect, useState, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import Plot from "react-plotly.js"
import { useTheme } from "@mui/material/styles"
import { ColorMode } from "@xyflow/react"
import { AxiosRequestConfig } from "axios"
import { fetchWithToken } from "api/data"
import { DdtData } from "api/param/param.data.pb"
import { default as routes} from "api/routes"
import { useTranslate, useLocale } from "ra-core"
import { DateRangePicker, Switcher } from "components/ui/forms"
import { ChartProps } from "./props"
import { ScreenEmpty } from "components/ui/screen"
import ru from "plotly.js/lib/locales/ru"

function DDTChartComponent(props: ChartProps & {showRangeslider: boolean}): JSX.Element {
    const { maxLimit, realtime, id, showDatePanel, showChartPanel, showRangeslider, containerStyle } = props
    const translate = useTranslate()
    const locale = useLocale()
    const theme = useTheme()
    const appColorMode = theme.palette.mode 
    const [colorMode, setColorMode] = useState<ColorMode>(appColorMode)
    const chartRef = useRef<HTMLDivElement>(null)
    const [dataapi, setDataapi] = useState<DdtData>({} as DdtData)
    const [online, setOnline] = useState<boolean>(realtime)

    const [xRange, setXRange] = useState<[Date | null, Date | null]>([null, null])

    //TODO: локаль зашита
    const locales = {
        ru,
    }

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            chartRef.current?.requestFullscreen()
        } else {
            document.exitFullscreen()
        }
    }

    const configAxios: AxiosRequestConfig = {
        url: routes.deviceChartData(id!, "current", maxLimit, !online ? xRange[0] : null, !online ? xRange[1] : null),
        method: 'get',
    }

    const { data: dataquery, refetch } = useQuery({
        queryKey: [`devices/${id}/chart`],
        queryFn: () => fetchWithToken(configAxios),
        refetchInterval: online ? 60000 : false, //TODO
    })

    useEffect(() => {
        if (dataquery) {
            setDataapi(dataquery.data as DdtData)
        }
    }, [dataquery])

    useEffect(() => {
        setColorMode(appColorMode)
    }, [appColorMode])

    useEffect(() => {
        refetch()
    }, [xRange, online, refetch])

    const isDark = colorMode === "dark"
    const textColor = isDark ? "#ffffff" : "#000000"
    const gridColor = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"

    const traceTemperature = {
        x: dataapi?.items?.length > 0 ? dataapi?.items?.map((d) => new Date(d.time)) : [],
        y: dataapi?.items?.length > 0 ? dataapi?.items?.map((d) => d.t) : [],
        type: 'scatter',
        mode: 'lines+markers',
        name: dataapi?.tlabel || "",
        yaxis: 'y1',
    }
    
    const tracePressure = {
        x: dataapi?.items?.length > 0 ? dataapi?.items?.map((d) => new Date(d.time)) : [],
        y: dataapi?.items?.length > 0 ? dataapi?.items?.map((d) => d.p) : [],
        type: 'scatter',
        mode: 'lines+markers',
        name: dataapi?.plabel || "",
        yaxis: 'y2',
    }

    const data = [traceTemperature, tracePressure]

    const layout = {
        dragmode: 'pan',
        xaxis: {
            type: 'date',
            rangeslider: { visible: showRangeslider },
            tickfont: { color: textColor },
            gridcolor: gridColor,
            //range: xRange,
        },
        yaxis: {
            title: dataapi?.tlabel || "",
            tickfont: { color: textColor },
            gridcolor: gridColor,
        },
        yaxis2: {
            title: dataapi?.plabel || "",
            overlaying: 'y',
            side: 'right',
            tickfont: { color: textColor },
            gridcolor: gridColor,
        },
        margin: { t: 50, b: 80, l: 60, r: 60 },
        
        legend: {
            orientation: 'h',    
            x: 0.5,              
            y: 1.1,              
            xanchor: 'center',   
            yanchor: 'bottom',   
            font: { color: textColor },
        },
        paper_bgcolor: isDark ? "#1e1e1e" : "#fff",
        plot_bgcolor: isDark ? "#1e1e1e" : "#fff",
    }

    const config = {
        displaylogo: false,
        responsive: true,
        modeBarButtons: showChartPanel ? [
            [{
                name: 'Toggle Fullscreen',
                title: translate("internal.fullscreen"),
                icon: {
                    width: 500,
                    height: 500,
                    path: 'M128 32H32C14.31 32 0 46.31 0 64v96c0 17.69 14.31 32 32 32s32-14.31 32-32V96h64c17.69 0 32-14.31 32-32S145.7 32 128 32zM416 32h-96c-17.69 0-32 14.31-32 32s14.31 32 32 32h64v64c0 17.69 14.31 32 32 32s32-14.31 32-32V64C448 46.31 433.7 32 416 32zM128 416H64v-64c0-17.69-14.31-32-32-32s-32 14.31-32 32v96c0 17.69 14.31 32 32 32h96c17.69 0 32-14.31 32-32S145.7 416 128 416zM416 320c-17.69 0-32 14.31-32 32v64h-64c-17.69 0-32 14.31-32 32s14.31 32 32 32h96c17.69 0 32-14.31 32-32v-96C448 334.3 433.7 320 416 320z'
                },
                click: function(gd: any) {
                    toggleFullScreen()
                },
            },],
            ['pan2d', 'zoom2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'toImage', 'resetScale2d'],
        ] : [[]],
        locale: locale,
        locales: locales,
    }

    const handleRelayout = (event: any) => {
        const startDateStr = event["xaxis.range[0]"]
        const endDateStr = event["xaxis.range[1]"]
        if (startDateStr && endDateStr) {
            //setXRange([new Date(startDateStr), new Date(endDateStr)])
        }
    }

    const defaultContainerStyle: React.CSSProperties = {
        backgroundColor: isDark ? "#1e1e1e" : "#fff",
        padding: "20px",
        borderRadius: "8px",
        height: "78vh",
        display: "flex",
        flexDirection: "column",
    }

    return (
        <div
            ref={chartRef}
            style={containerStyle || defaultContainerStyle}
        >
            <ScreenEmpty enabled={showDatePanel}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: "column",
                    }}
                >
                    <div>
                        <Switcher label={translate('internal.online')} defaultValue={online} onChangeCallback={(val) => setOnline(val)} /></div>
                        {!online && <div><DateRangePicker 
                            width="300px" 
                            onRangeChange={(dateRange) => {
                                if (dateRange[0] && dateRange[1]) {
                                    setXRange([dateRange[0], dateRange[1]])
                                } else if (!dateRange[0] && !dateRange[1]) {
                                    setXRange([null, null])
                                }
                            }} 
                        />
                    </div>}
                </div>
            </ScreenEmpty>
            <div style={{ flexGrow: 1 }}>
                <Plot 
                    data={data} 
                    layout={layout} 
                    style={{ width: '100%', height: '100%' }}
                    config={config}
                    onRelayout={handleRelayout}
                />
            </div>
        </div>
    )
}

DDTChartComponent.defaultProps = {
    maxLimit: 100,
    realtime: true,
    showDatePanel: true,
    showChartPanel: true,
    showRangeslider: true,
}

export default DDTChartComponent